/**
 * Tela /reset-password?token=... — recebe novo password e dispara reset.
 *
 * Token vem na query string. Após sucesso, redireciona pra /login depois
 * de 2s com toast confirmando.
 *
 * Validações no front (mín 8 chars + confirmação) replicam o backend pra
 * feedback imediato — backend faz validação autoritativa.
 *
 * O token é verificado NO SERVIDOR antes de abrir o formulário. Antes disso
 * a checagem era `token.length >= 10`, que só pega link truncado: link já
 * usado, expirado ou substituído por outro pedido abria o formulário
 * normalmente e só falhava depois de a pessoa digitar a senha nova — que é
 * o pior momento, porque ela fica sem saber se a senha valeu ou não.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, KeyRound, Lock } from "lucide-react";
import toast from "react-hot-toast";

import axios from "axios";

import { authApi } from "@/api/auth";
import { apiErrorMessage } from "@/api/client";
import { Button } from "@/components/Button";
import { BrandSymbol, BrandWordmark } from "@/components/Brand";


export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  // Mesmo link, duas portas: redefinição ("esqueci a senha") e convite de
  // usuário novo (e-mail de boas-vindas, `&convite=1`). Muda só o texto —
  // "Redefinir senha" pra quem nunca teve senha confunde.
  const convite = params.get("convite") === "1";

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Redireciona automaticamente após sucesso
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => navigate("/login", { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [done, navigate]);

  // `enabled` evita ida ao servidor com link obviamente truncado — o
  // formato ainda é checado aqui, só deixou de ser o único critério.
  const verificacao = useQuery({
    queryKey: ["reset-token", token],
    queryFn: () => authApi.verifyResetToken(token),
    enabled: token.length >= 10,
    retry: false,
    staleTime: Infinity,
  });

  const checando = token.length >= 10 && verificacao.isPending;
  const tokenValid = verificacao.isSuccess;
  // Só a API recusando (400) diz que o link morreu. Rede, 5xx ou limite de
  // requisições não dizem nada sobre o link — mandar pedir outro nesses
  // casos descartaria um link bom.
  const naoVerificou =
    verificacao.isError &&
    !(axios.isAxiosError(verificacao.error) && verificacao.error.response?.status === 400);
  const passwordValid = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirm && confirm.length > 0;
  const canSubmit = tokenValid && passwordValid && passwordsMatch && !submitting;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Token inválido ou expirado"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-full overflow-hidden">
      <div aria-hidden className="envpulse-bg pointer-events-none absolute inset-0 -z-20" />

      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="mb-10 flex flex-col items-center gap-5">
            <BrandSymbol className="h-16 w-16" glow />
            <BrandWordmark className="text-4xl" variant="light" />
          </div>

          <div
            className="rounded-2xl border border-white/10 bg-white/[0.025] p-7 backdrop-blur-xl md:p-8"
            style={{ boxShadow: "0 12px 60px -12px rgba(0,0,0,0.6)" }}
          >
            {checando ? (
              <div className="py-6 text-center">
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-cyan-400" />
                <p className="text-sm text-slate-400">Verificando o link…</p>
              </div>
            ) : naoVerificou ? (
              <div className="text-center">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/20">
                  <AlertTriangle className="h-6 w-6 text-amber-400" />
                </div>
                <h2 className="mb-2 text-xl font-semibold text-white">
                  Não deu pra conferir o link agora
                </h2>
                <p className="mb-6 text-sm text-slate-400">
                  O link pode estar valendo normalmente — o problema foi na conexão com o
                  servidor. Tente de novo em instantes.
                </p>
                <Button onClick={() => void verificacao.refetch()} loading={verificacao.isFetching}>
                  Tentar de novo
                </Button>
              </div>
            ) : !tokenValid ? (
              <div className="text-center">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-500/20">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <h2 className="mb-2 text-xl font-semibold text-white">
                  Link inválido
                </h2>
                <p className="mb-6 text-sm text-slate-400">
                  {convite
                    ? "Esse convite não vale mais — ele vence em 72 horas, vale uma vez só, e um convite reenviado substitui o anterior. Peça um novo a quem criou seu acesso, ou solicite um link abaixo."
                    : "Esse link não vale mais — pode ter expirado, já ter sido usado, ou ter sido substituído por um pedido mais recente. Solicite um novo pra continuar."}
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-500"
                >
                  Solicitar novo link
                </Link>
              </div>
            ) : done ? (
              <div className="text-center">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                </div>
                <h2 className="mb-2 text-xl font-semibold text-white">
                  {convite ? "Senha criada" : "Senha redefinida"}
                </h2>
                <p className="text-sm text-slate-400">
                  Redirecionando pro login em alguns segundos…
                </p>
              </div>
            ) : (
              <>
                <h2 className="mb-1 text-xl font-semibold text-white">
                  {convite ? "Crie sua senha" : "Nova senha"}
                </h2>
                <p className="mb-7 text-sm text-slate-400">
                  {convite
                    ? "Bem-vindo ao EnvPulse. Escolha uma senha forte, com pelo menos 8 caracteres — depois é só entrar com seu e-mail e ela."
                    : "Escolha uma senha forte com pelo menos 8 caracteres."}
                </p>

                <form onSubmit={onSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="new-password" className="label-dark">
                      Nova senha
                    </label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        id="new-password"
                        type="password"
                        required
                        autoComplete="new-password"
                        autoFocus
                        minLength={8}
                        className="input-dark pl-9"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="mínimo 8 caracteres"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirm" className="label-dark">
                      Confirmar senha
                    </label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        id="confirm"
                        type="password"
                        required
                        autoComplete="new-password"
                        className="input-dark pl-9"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="repita a senha"
                        disabled={submitting}
                      />
                    </div>
                    {confirm.length > 0 && !passwordsMatch && (
                      <p className="mt-1 text-[11px] text-red-400">
                        as senhas não coincidem
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    loading={submitting}
                    disabled={!canSubmit}
                    icon={!submitting ? <KeyRound className="h-4 w-4" /> : undefined}
                    className="w-full"
                  >
                    {convite ? "Criar senha" : "Redefinir senha"}
                  </Button>
                </form>
              </>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200 hover:underline"
              >
                <ArrowLeft className="h-3 w-3" />
                Voltar pro login
              </Link>
            </div>
          </div>

          <p className="mt-6 text-center text-[10px] text-slate-500">
            uma empresa{" "}
            <span className="font-semibold text-slate-300">LAIT</span>
            {" "}— Latin American Inovação e Tecnologia
          </p>
        </div>
      </div>
    </div>
  );
}
